import type { CommunityScorePercentageType } from "@/components/community-score-wheel";
import type { CategoryType } from "@/components/game-categories";
import type { CouponType } from "@/components/promotion-panel";
import type { EscapeType } from "@/graphql/mutations/use-update-escape";
import type { OverallRatingEnum } from "@/pages/search/types";

import type { EditorialReviewType } from "./editorial-reviews/types";

export const GameKindEnum = {
  RER: "Real Life Escape Room",
  TER: "Tabletop Escape Room",
  VER: "Virtual Escape Room",
} as const;
export type GameKindEnum = (typeof GameKindEnum)[keyof typeof GameKindEnum];

export const GameStatusEnum = {
  CS: "CS", // Coming Soon
  OP: "OP", // Open
  PC: "PC", // Permanently Closed
  TC: "TC", // Temporarily Closed
} as const;

export interface AddressType {
  address_components: {
    long_name: string;
    short_name: string;
    types: string[];
  }[];
  formatted_address: string;
}

export interface CompanyType {
  id: string;
  name: string;
  slug?: string;
  url: string;
}

export interface LocationType {
  address: AddressType;
  address2?: string;
  company: CompanyType;
  displayName?: string;
  id: string;
  isClaimed?: boolean;
  isPartnerLite: boolean;
  locality: {
    locality: string;
    region: string;
  };
  location: {
    type: string;
    coordinates: [number, number];
  };
  phoneNumber?: string;
  slug?: string;
  url?: string;
}

export interface AwardsType {
  category?: {
    source?: {
      awardName: string;
      sourceName: string;
    };
  };
  coarseUrl?: string;
  displayTitle: string;
  id: string;
}

export const ImageTypeEnum = {
  CALENDAR: "CALENDAR",
  IMAGE: "IMAGE",
  POSTER: "POSTER",
} as const;
export type ImageTypeEnum = (typeof ImageTypeEnum)[keyof typeof ImageTypeEnum];

export interface TagType {
  id: string;
  isCommunity?: boolean;
  name: string;
  parent?: {
    id: string;
    slug: string;
  };
  slug: string;
}

export interface GameType {
  additionalDetails: string;
  awards: AwardsType[];
  bookingUrl?: string;
  communityRatingCount?: number;
  communityScore?: number;
  communityScoreBucket?: string;
  communityScorePercentages: CommunityScorePercentageType[];
  communityScoreScaled?: number;
  componentScores: {
    communityScoreBucket?: string;
    question: {
      question: string;
      shortQuestion?: string;
    };
  }[];
  coupons: CouponType[];
  description: string;
  difficulty?: string;
  editorialReviews?: EditorialReviewType[];
  gameimageSet: {
    id: string;
    imageId: string;
    imageType: ImageTypeEnum;
  }[];
  handicapAccessibility: string;
  hasBookings: boolean;
  id: string;
  isCompleted?: boolean;
  isGoodForKids?: boolean;
  isHighTech?: boolean;
  isLowTech?: boolean;
  isSaved?: boolean;
  kind: keyof typeof GameKindEnum;
  location: LocationType;
  minimumAge: string;
  minutes: number;
  name: string;
  playersMax: number;
  playersMin: number;
  primaryCategory: CategoryType;
  privacy?: string;
  secondaryCategories: CategoryType[];
  slug: string;
  status: keyof typeof GameStatusEnum;
  tags: TagType[];
  url?: string;
  userReview?: {
    id: string;
  }[];
}

export interface GameFriendType {
  escapeReview?: {
    id: string;
    hasSummary: boolean;
    overallRating?: keyof typeof OverallRatingEnum;
  };
  id: string;
  imageId?: string;
  username: string;
}

export interface UserGameType {
  friendsEscaped: GameFriendType[];
  friendsWishlisted: GameFriendType[];
  id: string;
  isCompleted?: boolean;
  isSaved?: boolean;
  myEscape?: EscapeType;
  overallRating?: keyof typeof OverallRatingEnum;
}
